(ns arthur.flow.detect
  "The only MediaPipe boundary. Landmarks leave here as ordinary CLJS values.")

(defonce ^:private instance (atom nil))
(defonce ^:private pending (atom nil))

(def settings
  "Detection and assignment inputs, also included in the analysis address."
  {:max-faces 4 :assignment "nearest-centroid-v1" :gate 0.2})

(defn discard!
  "Throw away the cached landmarker so the next run builds a fresh one.

  Because a MediaPipe graph error is PERMANENT for the instance that hit it. A
  timestamp that did not advance leaves the graph in an error state, and every
  later `detectForVideo` on that landmarker re-throws it — so a memoised instance
  turns one bad run into a tool that is broken until the tab is reloaded. Called
  from the failure path, not from the happy one: a landmarker costs 26MB of wasm
  and a model parse, and that is worth keeping for a run that ended cleanly."
  []
  (when-let [model @instance]
    (try (.call (aget model "close") model) (catch :default _ nil)))
  (reset! instance nil)
  (reset! pending nil))

(defn landmarker!
  "Initialize once, using the vendored wasm and the local model. CPU also works
  in browsers where a GPU delegate initializes but fails on its first frame.

  Under `/static/` since step 9: the assets still live in `frontend/public/mediapipe`
  — 26MB of wasm and model that has no business being copied into a second place in
  the tree — and Django's staticfiles serves that directory under the `mediapipe/`
  prefix. Still no CDN, which is the property that matters: the only thing in this
  tool that would silently require a network is the one thing that must not."
  []
  (if-let [model @instance]
    (js/Promise.resolve model)
    (or @pending
        (if-let [vision (aget js/window "Vision")]
          (let [resolver (aget vision "FilesetResolver")
                landmarker (aget vision "FaceLandmarker")
                ready (-> (.call (aget resolver "forVisionTasks") resolver "/static/mediapipe/wasm")
                        (.then (fn [fileset]
                                 (.call (aget landmarker "createFromOptions")
                                        landmarker fileset
                                        #js {:baseOptions
                                             #js {:modelAssetPath "/static/mediapipe/face_landmarker.task"
                                                  :delegate "CPU"}
                                             :runningMode "VIDEO"
                                             :numFaces (:max-faces settings)})))
                        (.then (fn [model]
                                 (reset! instance model)
                                 model))
                        (.catch (fn [e]
                                  (reset! pending nil)
                                  (throw e))))]
            (reset! pending ready)
            ready)
          (js/Promise.reject (js/Error. "local MediaPipe script did not load"))))))

(defn detect!
  "Detect one already-drawn canvas frame at its own time in the take. An empty
  vector means no face was detected.

  `at-ms` IS THE FRAME'S REAL PRESENTATION TIME, and all three words are
  load-bearing. In VIDEO mode the graph is a tracker: it runs face DETECTION only
  when it has lost the face, and otherwise follows the previous frame's region,
  using the gap between timestamps as the motion it has to account for. So:

  IT MUST INCREASE, STRICTLY. MediaPipe's input streams reject a timestamp that
  does not advance — `Packet timestamp mismatch on a calculator receiving from
  stream \"norm_rect\"` — and that error is not recoverable: the graph is left in an
  error state and every later call on this landmarker throws the same thing. One
  repeated frame kills the run, so the caller walks frames forward exactly once.

  IT MUST BE MILLISECONDS OF FOOTAGE, not a frame counter. Feeding `i` instead of
  `i * 1000 / fps` still runs — and measured over the same 91 frames it moved
  landmarks six times further from the per-frame answer (0.079 of frame width
  against 0.013), because a tracker told that every frame is 1ms apart expects a
  face that has barely moved."
  [model canvas at-ms]
  (let [faces (aget (.call (aget model "detectForVideo") model canvas at-ms)
                    "faceLandmarks")]
    (mapv (fn [face] (mapv (fn [p] {:x (.-x p) :y (.-y p) :z (.-z p)}) face))
          (array-seq faces))))

;; ---------------------------------------------------------------------------
;; which face is which
;;
;; MediaPipe hands back a LIST, and a list has an order rather than an identity.
;; Nothing in the API promises that slot 0 is the same person on frame 41 as on
;; frame 40 — and when two faces cross, or one is briefly lost and re-detected,
;; it is not. Left unassigned, the two faces' geometry would swap mid-shot inside
;; one dense block, which reads as both heads snapping and is invisible in any
;; per-frame assertion.
;;
;; So identity is assigned HERE, once, by nearest centroid to each track's last
;; known position. Greedy and cheap: a handful of faces, one pass, and the
;; ordering it produces is the `:subjects` map every later stage keys on.

(defn- centroid [face]
  (let [n (count face)]
    {:x (/ (reduce + (map :x face)) n)
     :y (/ (reduce + (map :y face)) n)}))

(defn- distance [a b]
  (let [dx (- (:x a) (:x b)) dy (- (:y a) (:y b))]
    (js/Math.sqrt (+ (* dx dx) (* dy dy)))))

(defn assign
  "Per-frame lists of faces -> one vector per TRACK of `detection index or nil`.

  INDICES AND NOT FACES, because a detection is more than its landmarks: the
  mouth crop and the pixel measurement taken beside it on the same frame have to
  follow the same face, and handing back the index is what lets one assignment
  re-key all three. Whoever holds the per-frame lists does the lookup.

  A track is claimed by the unclaimed detection nearest its last known centroid,
  nearest pair first, so a frame where MediaPipe swaps its slot order does not
  swap the tracks. A detection that matches no existing track within `gate`
  starts a new one — which is how a second person walking into the shot on frame
  200 gets their own subject instead of stealing the first one's.

  `gate` is in normalised image units: two centroids closer than this on
  consecutive frames are one face moving, and further apart are not."
  ([frames] (assign frames (:gate settings)))
  ([frames gate]
   (let [step (fn [[rows last] faces]
                (let [cs (mapv centroid faces)
                      pairs (sort-by :d
                                     (for [[t at] (map-indexed vector last)
                                           :when at
                                           [d c] (map-indexed vector cs)
                                           :let [gap (distance at c)]
                                           :when (< gap gate)]
                                       {:d gap :track t :face d}))
                      claim (reduce (fn [{:keys [by-track taken] :as acc}
                                         {:keys [track face]}]
                                      (if (or (contains? by-track track)
                                              (contains? taken face))
                                        acc
                                        {:by-track (assoc by-track track face)
                                         :taken (conj taken face)}))
                                    {:by-track {} :taken #{}}
                                    pairs)
                      opened (map-indexed (fn [i d] [(+ (count last) i) d])
                                          (remove (:taken claim) (range (count faces))))
                      by-track (into (:by-track claim) opened)
                      width (+ (count last) (count opened))]
                  [(conj rows (mapv by-track (range width)))
                   (mapv (fn [t] (if-let [d (get by-track t)]
                                   (nth cs d)
                                   (nth last t nil)))
                         (range width))]))
         [rows _] (reduce step [[] []] frames)
         width (reduce max 0 (map count rows))]
     ;; Transposed to column-major: one vector per subject, padded to the final
     ;; width so a track that opened late still spans the whole take.
     (mapv (fn [t] (mapv (fn [row] (nth row t nil)) rows))
           (range width)))))

(defn fill-gaps
  "Keep the detection mask while supplying real poses for measurement. A leading
  gap uses the first observed face; later gaps hold the previous observed pose.
  Freeze uses the mask to mark those frames absent in the channel blocks.

  ONE TRACK, which is one subject: the mask says when THIS face was on screen,
  and two faces in a shot have two of them. A frame where the second person has
  not walked in yet is a frame their track is absent on, which is the same fact
  as a frame nobody was found on and needs no second mechanism."
  [raw]
  (let [first-real (first (keep-indexed (fn [i frame] (when frame i)) raw))]
    (when-not first-real
      (throw (ex-info "no face found in any frame — check framing and light" {})))
    {:dense (loop [i 0 last-face (nth raw first-real) out []]
              (if (= i (count raw))
                out
                (let [face (or (nth raw i) last-face)]
                  (recur (inc i) face (conj out face)))))
     :detected (mapv some? raw)
     :missing (count (remove some? raw))
     :first-real first-real}))

(defn subject-id
  "Track index -> the subject that owns it. `:face-1` is the first face found,
  which is also the id a single-face take has always used."
  [i]
  (keyword (str "face-" (inc i))))

(defn tracks
  "Per-frame detection lists -> `{:face-1 <slots>, …}`, one entry per tracked
  face, each a per-frame detection index or nil."
  [frames]
  (let [assigned (assign frames)]
    (when (empty? assigned)
      (throw (ex-info "no face found in any frame — check framing and light" {})))
    (into {} (map-indexed (fn [i slots] [(subject-id i) slots])) assigned)))

(defn pick
  "One track's slots applied to anything measured PER DETECTION — the landmarks,
  the mouth crop, the pixel measurement taken beside it. All three were recorded
  in detection order on a frame where nobody yet knew whose face was whose, and
  this is the single lookup that turns any of them into one subject's track."
  [per-frame slots]
  (mapv (fn [row slot] (when slot (nth row slot))) per-frame slots))
